import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useHalachicData } from '../../hooks/useHalachicData';
import templates from './templates';

/**
 * ParnasWidget
 * מציג את הפרנסים הפעילים כעת (יומי / שבועי / חודשי).
 * משתמש במנוע ההלכתי לקבלת תאריך עברי, פרשה וזמן שקיעה.
 */
export const ParnasWidget = ({
  widgetId,
  // Legacy props (no longer used)
  parnasData: legacyParnasData,
  loading: legacyLoading,
  error: legacyError,
  dispatch: legacyDispatch,
  location,
  sponsors              = [],
  parnasType            = 'maor',
  parnasTypeCustomLabel = '',
  parnasTypeIcon        = 'star',
  template              = 'Tiles',
  title                 = 'פרנס הקהילה',
  showTitle             = false,
  tileColors            = undefined,
  placeholderName       = undefined,
  placeholderNote       = undefined,
  weeklyYearDisplayYear = undefined,
  weeksAheadCount       = undefined,
  honorCardPriority     = undefined,
  includedSlotTypes     = undefined,
}) => {
  // ── שליפה מהמנוע ההלכתי ──────────────────────────────────────────────
  const engineConfig = {
    location: {
      lat: location?.lat,
      lng: location?.lng,
      timezone: location?.timezone || location?.tzid || 'Asia/Jerusalem',
    },
    isIsrael: true,
    edah: 'ashkenaz',
  };

  const { today, loading: engineLoading, error: engineError } = useHalachicData(engineConfig);

  // ── חישוב פרנסים פעילים מתוך המנוע ──────────────────────────────────
  const parnasResult = useMemo(() => {
    if (!today) return null;

    const hd = today.hebrewDate;
    const todayHMonth = hd.month;
    const todayHYear = hd.year;
    const todayHDay = hd.day;
    const todayHebrewLabel = hd.hebrew;
    const effectiveDateISO = today.date;

    // פרשה — מהמנוע, מחשב תאריך שבת
    const currentParshaName = today.parsha?.name || '';
    // חישוב תאריך השבת הקרוב
    const todayDate = new Date(effectiveDateISO + 'T12:00:00');
    const dayOfWeek = todayDate.getDay(); // 0=Sun, 6=Sat
    const daysUntilSat = (6 - dayOfWeek + 7) % 7 || 7;
    const satDate = new Date(todayDate);
    if (dayOfWeek !== 6) {
      satDate.setDate(satDate.getDate() + daysUntilSat);
    }
    const currentParshaShabbatDate = `${satDate.getFullYear()}-${String(satDate.getMonth()+1).padStart(2,'0')}-${String(satDate.getDate()).padStart(2,'0')}`;

    // חישוב פרנסים פעילים
    const activeSponsors = [];
    for (const sponsor of sponsors) {
      for (const slot of (sponsor.slots || [])) {
        let isActive = false;

        if (slot.type === 'daily') {
          isActive = slot.date === effectiveDateISO;
        } else if (slot.type === 'weekly') {
          isActive = !!(currentParshaShabbatDate && slot.parshaShabbatDate === currentParshaShabbatDate);
        } else if (slot.type === 'monthly') {
          isActive = todayHMonth !== null
            && slot.hebrewMonth === todayHMonth
            && slot.hebrewYear  === todayHYear;
        }

        if (isActive) {
          activeSponsors.push({
            sponsorName: sponsor.name,
            slotType:    slot.type,
            note:        slot.note  || '',
            icon:        slot.icon  || null,
          });
        }
      }
    }

    return {
      activeSponsors,
      currentParshaShabbatDate,
      currentParshaName,
      todayHMonth,
      todayHYear,
      todayHDay,
      todayHebrewLabel,
      effectiveDateISO,
    };
  }, [today, sponsors]);

  const Template = templates[template] || templates['Tiles'];

  if (engineLoading && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={24} sx={{ color: '#1e3a5f' }} />
      </Box>
    );
  }

  if (engineError && !today) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#ef4444' }}>שגיאה בטעינת הנתונים</Typography>
      </Box>
    );
  }

  return (
    <Template
      activeSponsors={parnasResult?.activeSponsors || []}
      parnasType={parnasType}
      parnasTypeCustomLabel={parnasTypeCustomLabel}
      parnasTypeIcon={parnasTypeIcon}
      currentParshaName={parnasResult?.currentParshaName || ''}
      todayHebrewLabel={parnasResult?.todayHebrewLabel || ''}
      title={title}
      showTitle={showTitle}
      tileColors={tileColors}
      placeholderName={placeholderName}
      placeholderNote={placeholderNote}
      sponsors={sponsors}
      effectiveDateISO={parnasResult?.effectiveDateISO || ''}
      currentParshaShabbatDate={parnasResult?.currentParshaShabbatDate || ''}
      todayHYear={parnasResult?.todayHYear || null}
      todayHMonth={parnasResult?.todayHMonth || null}
      todayHDay={parnasResult?.todayHDay || null}
      weeklyYearDisplayYear={weeklyYearDisplayYear}
      weeksAheadCount={weeksAheadCount}
      honorCardPriority={honorCardPriority}
      includedSlotTypes={includedSlotTypes}
    />
  );
};

export default ParnasWidget;
